<template>
    <div>
        <div class="row">
            <div class="position-relative w-100">
                <h4 class="py-3">Funding Sources: {{ title }}</h4>
                <slot></slot>

                <div class="row">
                    <div class="col-md-6">
                        <paginator :pagination="pagination" @fetch="fetchPage($event)"></paginator>
                    </div>
                    <div class="col-md-6 d-flex justify-content-end align-items-center">
                        <transition name="simple-fade">
                            <small
                                v-if="exportError"
                                class="text-primary font-italic text-nowrap mx-2"
                                style="display:block"
                                >{{ exportError }}</small
                            >
                        </transition>

                        <button
                            :disabled="loadingDownloadFunding"
                            @click="exportInvoice()"
                            class="btn btn-link font-weight-bold text-uppercase p-0"
                        >
                            <i v-if="!loadingDownloadFunding" class="fa fa-download"></i>
                            <i
                                v-if="loadingDownloadFunding"
                                class="fa fa-circle-o-notch fa-spin"
                            ></i>
                            Export
                        </button>

                        <button
                            v-if="['Private Pay', 'Insurance Providers'].includes(title)"
                            class="btn float-right btn-sm btn-primary text-white text-uppercase ml-3"
                            @click="openInvoiceMiscChargeModal()"
                        >
                            Create Invoice
                        </button>

                        <button
                            v-if="title === 'Private Pay'"
                            class="btn float-right btn-sm btn-primary text-white text-uppercase ml-3"
                            @click="openAddPaymentModal()"
                        >
                            Make Payments
                        </button>
                    </div>
                </div>

                <div
                    class="alert alert-info alert-dismissible fade show d-flex align-items-center"
                    role="alert"
                >
                    <button
                        class="btn btn-link text-info py-0 px-1"
                        @click="showClaimInfoModal(true)"
                    >
                        Click here
                    </button>
                    if you need information on how to submit insurance claims.
                    <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <claim-info
                    :is-visible="isClaimInfoModalVisible"
                    :claim-error="claimError"
                    @close-modal="showClaimInfoModal(false)"
                ></claim-info>

                <div
                    class="table-responsive"
                    :style="localInvoices.length ? 'min-height:330px' : ''"
                >
                    <table class="table table-striped table-sm">
                        <thead class="thead-dark">
                            <tr>
                                <th
                                    class="text-nowrap ellipsis"
                                    :class="`text-${f.align}`"
                                    v-for="f in fields"
                                >
                                    {{ f.title }}
                                </th>
                                <th
                                    class="text-nowrap ellipsis"
                                    :class="`text-right`"
                                    v-if="title !== 'Private Pay'"
                                >
                                    Verified Paid Amount
                                </th>
                                <th v-else-if="loading"><!--filler--></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td v-for="f in fields.length + 2">
                                    <i class="text-center fa fa-spin fa-circle-o-notch mr-2"></i>
                                </td>
                            </tr>
                            <tr v-for="(invoice, indexMain) in localInvoices">
                                <td v-for="f in fields" :class="`text-${f.align}`">
                                    <span class="" v-if="f.prop === 'paid_amount'"
                                        >${{
                                            helpers._get(invoice, "paid_amount", false).toFixed(2)
                                        }}</span
                                    ><span v-else-if="f.prop === 'serviced_at'">{{
                                        invoice.serviced_at
                                            ? moment(invoice.serviced_at).format("MM/DD/YYYY")
                                            : ""
                                    }}</span>
                                    <span v-else-if="f.prop === 'expected_amount'"
                                        >${{
                                            helpers._get(invoice, f.prop, false).toFixed(2)
                                        }}</span
                                    ><span v-else-if="f.prop === 'service_hours'">{{
                                        invoice[f.prop] ? invoice[f.prop] : ""
                                    }}</span>
                                    <span
                                        v-else-if="f.prop === 'last_claim_internal_status'"
                                        class="text-capitalize badge"
                                        v-tooltip="getStatusTooltipText(invoice)"
                                        :class="invoice[f.prop] ? invoice[f.prop] : ''"
                                        >{{
                                            invoice[f.prop] ? invoice[f.prop].replace("_", " ") : ""
                                        }}</span
                                    >
                                    <span v-else-if="f.prop !== 'therapists'">
                                        <i
                                            v-if="
                                                f.prop === 'therapists[0].name' &&
                                                    isTherapistArchived(
                                                        helpers._get(
                                                            invoice,
                                                            'therapists[0].id',
                                                            -1
                                                        )
                                                    )
                                            "
                                            class="fa fa-exclamation-triangle text-danger fa-fw"
                                            v-tooltip="
                                                'This therapist has been archived. Please review this invoice.'
                                            "
                                        ></i>
                                        {{ helpers._get(invoice, f.prop, false) }}</span
                                    >

                                    <span
                                        v-else=""
                                        class="comma-list"
                                        v-for="t in helpers._get(invoice, f.prop, false)"
                                    >
                                        {{ t.name }}
                                    </span>
                                </td>

                                <template v-if="!['Paid', 'Private Pay'].includes(title)">
                                    <td class="d-flex justify-content-end">
                                        <div
                                            class="input-group input-group-sm d-flex justify-content-end"
                                        >
                                            <div class="input-group-prepend">
                                                <div class="input-group-text">$</div>
                                            </div>
                                            <input
                                                type="text"
                                                style="max-width:5rem;"
                                                placeholder="0.00"
                                                class="form-control form-control-sm text-right"
                                                v-model.number="invoice.paid_amount"
                                                @input="$set(invoice, 'changed', true)"
                                            />
                                        </div>
                                    </td>
                                </template>

                                <template v-if="['Paid'].includes(title)">
                                    <td class="text-right">
                                        <span
                                            >${{
                                                helpers
                                                    ._get(invoice, "paid_amount", false)
                                                    .toFixed(2)
                                            }}</span
                                        >
                                    </td>
                                </template>

                                <td class="text-right">
                                    <template>
                                        <div
                                            class="btn-group"
                                            role="group"
                                            aria-label="Main action and dropdown"
                                        >
                                            <button
                                                type="button"
                                                :id="`action-${invoice.id}`"
                                                class="btn btn-sm btn-primary"
                                                :disabled="
                                                    !!badInvoiceData(
                                                        invoice,
                                                        actionTitle(indexMain, 0)
                                                    )
                                                "
                                                style="width: 8rem;"
                                                @click="actionMethod(invoice, indexMain, 0)"
                                                v-tooltip="
                                                    badInvoiceData(
                                                        invoice,
                                                        actionTitle(indexMain, 0)
                                                    )
                                                "
                                            >
                                                <i
                                                    class="fa fa-exclamation-triangle mr-2"
                                                    v-if="
                                                        badInvoiceData(
                                                            invoice,
                                                            actionTitle(indexMain, 0)
                                                        )
                                                    "
                                                ></i>
                                                {{ actionTitle(indexMain, 0) }}
                                            </button>
                                            <div class="btn-group" role="group">
                                                <button
                                                    id="dropdown"
                                                    type="button"
                                                    class="btn btn-sm btn-primary dropdown-toggle"
                                                    data-toggle="dropdown"
                                                    aria-haspopup="true"
                                                    aria-expanded="false"
                                                ></button>
                                                <div
                                                    class="dropdown-menu dropdown-menu-right"
                                                    aria-labelledby="dropdown"
                                                >
                                                    <button
                                                        v-for="(a, indexSub) in availableActions[
                                                            indexMain
                                                        ]"
                                                        :class="{
                                                            'text-danger':
                                                                actionTitle(indexMain, indexSub) ===
                                                                'Delete invoice',
                                                            'text-muted': badInvoiceData(
                                                                invoice,
                                                                actionTitle(indexMain, indexSub)
                                                            ),
                                                        }"
                                                        class="cursor-pointer dropdown-item small"
                                                        @click.prevent="
                                                            badInvoiceData(
                                                                invoice,
                                                                actionTitle(indexMain, indexSub)
                                                            )
                                                                ? null
                                                                : actionMethod(
                                                                      invoice,
                                                                      indexMain,
                                                                      indexSub
                                                                  )
                                                        "
                                                        :disabled="
                                                            badInvoiceData(
                                                                invoice,
                                                                actionTitle(indexMain, indexSub)
                                                            )
                                                        "
                                                    >
                                                        <i
                                                            class="fa fa-exclamation-triangle mr-2"
                                                            v-if="
                                                                badInvoiceData(
                                                                    invoice,
                                                                    actionTitle(indexMain, indexSub)
                                                                )
                                                            "
                                                        ></i>

                                                        {{ actionTitle(indexMain, indexSub) }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <paginator :pagination="pagination" @fetch="fetchPage($event)"></paginator>
                    </div>
                </div>
            </div>
        </div>

        <p v-if="!loading && (localInvoices && !localInvoices.length)" class="font-italic">
            No invoice matches the current search parameters.
        </p>

        <modal-component
            :id="'funding-sources-modal'"
            :visible="modal.visible"
            :title="modal.title"
            :size="modal.size"
            @close="resetModal()"
        >
            <template>
                <table class="table table-striped" v-if="invoice.hasOwnProperty('charges')">
                    <thead class="thead-dark">
                        <tr>
                            <th>ID</th>
                            <th>Codes</th>
                            <th>Therapist</th>
                            <th>Service hrs</th>
                            <th>Expected amount</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="c in invoice.charges">
                            <td>{{ c.id }}</td>
                            <td>
                                <span class="comma-list small" v-for="code in c.codes">{{
                                    `${code.code} - ${code.service_nickname}`
                                }}</span>
                            </td>
                            <td>{{ c.therapist_name }}</td>
                            <td>{{ c.service_hours ? c.service_hours : "" }}</td>
                            <td>{{ c.expected_amount }}</td>
                            <td>{{ moment(c.service_start_at).format("MM/DD/YYYY") }}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="d-flex justify-content-end">
                    <button class="btn btn-sm btn-primary text-white" @click="resetModal()">
                        Close
                    </button>
                </div>
            </template>
        </modal-component>

        <invoice
            ref="invoiceModal"
            :organization="organization"
            :invoice="invoice"
            @close="$refs.invoiceModal.visible = false"
            :is-invoice="title !== 'Paid'"
        ></invoice>
        <claim ref="claimModal" :claims="claims" @update-invoice="updateInvoice($event)"></claim>
        <add-payment
            ref="addPaymentModal"
            :organization="organization"
            :patients="patients"
            :invoices="invoices"
            @close="$refs.addPaymentModal.resetModal()"
            @fetch-invoices="$emit('fetch-invoices')"
        ></add-payment>
        <invoice-misc-charge
            ref="invoiceMiscCharge"
            :organization="organization"
            :title="title"
            :patients="patients"
            :therapists="therapists"
            :claim="helpers._get(claims, '0', {})"
            :places-of-service="placesOfService"
            @fetch-page="$emit('fetch-page')"
            @local-update="localUpdateInvoice($event)"
            @resubmit-claim="submitClaim($event)"
            @close="$refs.invoiceMiscCharge.resetModal()"
        ></invoice-misc-charge>
        <cant-submit-claim ref="cantSubmitClaim"></cant-submit-claim>

        <errors :errors="errors"></errors>

        <div class="my-2">
            <div v-if="success" class="alert alert-success">
                <a href="#" class="close" data-dismiss="alert">&times;</a>
                <span>{{ success }}</span>
            </div>
        </div>

        <delete-confirmation
            :visible="deleteConfirmationVisible"
            :button-text="'Yes, delete invoice'"
            @confirm="deleteInvoice(deletePayload)"
            @close="resetDelete()"
        >
            <template slot="title"
                >Delete invoice?</template
            >
            <template slot="description"
                >This action cannot be undone.</template
            >
        </delete-confirmation>
    </div>
</template>
<script>
import { helpers } from "../../plugins/helpers";
import { errors } from "../../plugins/errors";
import { fixDatePickers } from "../../plugins/fixDatePickers";
import { permissions } from "../../plugins/permissions";
import { deleteConfirmation } from "../../plugins/deleteConfirmation";
import ModalComponent from "../ui/ModalComponent";
import Dots from "../ui/DotDotDot";
import Invoice from "./Invoice";
import Claim from "./Claim";
import Paginator from "../ui/Paginator";
import AddPayment from "./AddPayment";
import CantSubmitClaim from "./CantSubmitClaim";
import InvoiceMiscCharge from "./InvoiceMiscCharge";
import ClaimInfo from "./ClaimInfo";

export default {
    name: "FundingSource",
    props: [
        "organization",
        "title",
        "fields",
        "patients",
        "therapists",
        "invoices",
        "pagination",
        "loading",
        "sourceType",
        "placesOfService",
    ],
    mixins: [errors, permissions, fixDatePickers, deleteConfirmation],
    components: {
        ModalComponent,
        Invoice,
        Dots,
        Claim,
        Paginator,
        AddPayment,
        CantSubmitClaim,
        InvoiceMiscCharge,
        ClaimInfo,
    },
    data() {
        return {
            helpers,
            search: null,
            invoicesToMove: [],
            modal: {
                visible: false,
                size: "lg",
                id: "invoice-charges",
                title: "",
            },
            success: false,
            paymentOptions: [
                { title: "Check", value: "check" },
                { title: "Cash", value: "cash" },
                { title: "Prepayment", value: "prepayment" },
                { title: "Card on file", value: "credit_card" },
                { title: "Credit card", value: "credit_card" },
            ],
            claims: [],
            invoice: { patient: {}, therapists: [{}] }, //This is changed by a child component
            loadingDownloadFunding: false,
            exportError: "",
            isClaimInfoModalVisible: false,
            claimError: null,
        };
    },

    computed: {
        localInvoices() {
            // when the parent changes the invoices
            // copy it over to the local version
            return this.invoices;
        },

        availableActions() {
            let availableActions = [];
            this.localInvoices.forEach((invoice, index) => {
                availableActions[index] = [];
                switch (this.title) {
                    case "Schools":
                    case "Easterseals":
                    case "Regional Centers":
                        availableActions[index].push("View invoice");
                        availableActions[index].push("Edit invoice");
                        availableActions[index].push("Update");
                        availableActions[index].push("Move to paid");
                        availableActions[index].push("Delete invoice");
                        break;
                    case "Insurance Providers":
                        if (
                            this.canSubmitClaim &&
                            !invoice.has_submitted_claim &&
                            invoice.can_submit_claim &&
                            invoice.is_provider_enabled
                        ) {
                            availableActions[index].push("Submit claim");
                        }

                        if (
                            invoice.has_submitted_claim &&
                            invoice.can_submit_claim &&
                            invoice.is_provider_enabled &&
                            invoice.last_claim_internal_status !== "paid"
                        ) {
                            availableActions[index].push("Resubmit claim");
                        }
                        if (invoice.has_submitted_claim) {
                            availableActions[index].push("View claim");
                        }
                        if (
                            invoice.has_submitted_claim &&
                            invoice.is_provider_enabled &&
                            ["paid", "partial_rejection", "rejected"].includes(
                                invoice.last_claim_internal_status
                            )
                        ) {
                            availableActions[index].push("Resubmit to new contract");
                        }
                        availableActions[index].push("View invoice");
                        availableActions[index].push("Edit invoice");
                        availableActions[index].push("Update");
                        availableActions[index].push("Move to paid");
                        availableActions[index].push("Delete invoice");
                        break;
                    case "Private Pay":
                        availableActions[index].push("Make payment");
                        availableActions[index].push("View invoice");
                        availableActions[index].push("Edit invoice");
                        availableActions[index].push("Move to paid");
                        availableActions[index].push("Delete invoice");
                        break;
                    case "Paid":
                        availableActions[index].push("View receipt");
                        availableActions[index].push("Move to unpaid");
                }
                if (invoice.changed) {
                    const indexPaidAmount = availableActions[index].indexOf("Update");
                    if (indexPaidAmount > 0) {
                        availableActions[index].unshift(
                            availableActions[index].splice(indexPaidAmount, 1)[0]
                        );
                    }
                }
            });
            return availableActions;
        },

        availableTherapistIds() {
            return this.therapists.map(therapist => therapist.id);
        },
    },

    watch: {
        exportError() {
            if (this.exportError) {
                const context = this;
                setTimeout(() => {
                    context.exportError = "";
                }, 5000);
            }
        },
    },

    methods: {
        getStatusTooltipText(invoice) {
            switch (invoice.last_claim_internal_status) {
                case "pending":
                    return "Claim has been submitted.";
                case "acknowledged":
                    if (invoice.can_check_claim_status == false) {
                        return "Claim has been accepted but this insurance company does not support automatic claim status. Please contact insurance company for adjudication status.";
                    }
                    return "Claim has been accepted and awaiting adjudication.";
                case "rejected":
                    return "Claim has been rejected.";
                case "approved":
                    return "Claim has been approved and awaiting payment.";
                case "paid":
                    return "Claim has been paid.";
            }

            return false;
        },
        actionTitle(indexMain, indexSub) {
            return this.availableActions[indexMain][indexSub];
        },

        actionMethod(invoice, indexMain, indexSub) {
            const title = this.actionTitle(indexMain, indexSub);
            switch (title) {
                case "Make payment":
                    this.openPrivatePayModal(invoice);
                    return;
                case "Submit claim":
                    this.submitClaim(invoice.id);
                    return;
                case "Resubmit claim":
                    this.editInvoice(invoice);
                    return;
                case "Resubmit to new contract":
                    this.editInvoice(invoice, true);
                    return;
                case "View claim":
                    this.viewClaim(invoice);
                    return;
                case "Update":
                    this.handleSave(invoice, invoice.id, invoice.paid_amount);
                    return;
                case "View invoice":
                case "View receipt":
                    this.viewInvoice(invoice);
                    return;
                case "Edit invoice":
                    this.editInvoice(invoice);
                    return;
                case "Delete invoice":
                    this.promptDelete(invoice);
                    return;
                case "Move to paid":
                    this.handleMoveToPaid([invoice.id]);
                    return;
                case "Move to unpaid":
                    this.handleMoveToUnpaid([invoice.id]);
                    return;
            }
        },

        openChargesModal(invoice) {
            this.invoice = invoice;
            this.modal.title = `${invoice.patient.name} - Invoice #${invoice.id} charges`;
            this.modal.id = `invoice-${invoice.id}-charges`;
            this.modal.visible = true;
        },

        openAddPaymentModal() {
            this.$refs.addPaymentModal.visible = true;
        },

        openPrivatePayModal(invoice) {
            this.fetchInvoice(invoice.id);
            this.$refs.addPaymentModal.visible = true;
            this.$refs.addPaymentModal.patient_id = invoice.patient.id;
            this.$refs.addPaymentModal.invoice_id = invoice.id;
        },

        openInvoiceMiscChargeModal() {
            this.$refs.invoiceMiscCharge.newContract = false;
            this.$refs.invoiceMiscCharge.visible = true;
        },

        resetModal() {
            this.success = false;
            this.invoice = {};
            this.claims = [];
            this.modal = {
                visible: false,
                size: "lg",
                id: "",
                title: "",
            };
        },

        handleSave(invoice, id, paid_amount) {
            this.$emit("update-invoice", [id, paid_amount]);
        },

        handleMoveToPaid(idArray) {
            this.$emit("move-invoices", idArray);
        },

        handleMoveToUnpaid(idArray) {
            this.$emit("recover-invoices", idArray);
        },

        fetchPage(event) {
            this.$emit("fetch-page", event);
        },

        openCantSubmitClaimModal(patient) {
            this.$refs.cantSubmitClaim.patient = patient;
            this.$refs.cantSubmitClaim.visible = true;
        },

        patientCanSubmitClaim(invoice) {
            if (!_.get(invoice, "patient.gender", false) || !_.get(invoice, "patient.dob", false)) {
                this.openCantSubmitClaimModal(invoice.patient);
                return false;
            }
            return true;
        },

        async submitClaim(invoiceId) {
            this.claimError = null;
            this.setErrors();
            const invoice = await this.fetchInvoiceAsync(invoiceId);
            if (!this.patientCanSubmitClaim(invoice)) return;
            helpers.toggleSpinner(`action-${invoiceId}`);
            axios
                .post(`/api/organizations/${this.organization.id}/invoices/${invoiceId}/claims`)
                .then(r => {
                    switch (r.data.status) {
                        case "error":
                            this.$swal(
                                "Error",
                                "The insurance claim submission has failed.",
                                "error"
                            );
                            break;
                        case "success":
                            this.$swal(
                                "Done!",
                                "Insurance claim submitted successfully.",
                                "success"
                            );
                            this.$emit("local-update", r.data.data);
                            break;
                    }
                    helpers.toggleSpinner(`action-${invoiceId}`);
                })
                .catch(e => {
                    this.claimError = _.get(
                        e,
                        "response.data.message",
                        "The insurance claim submission has failed."
                    );
                    this.$swal({
                        title: "Error",
                        text: this.claimError,
                        type: "error",
                        showCloseButton: true,
                        showCancelButton: true,
                        cancelButtonText: "OK",
                        confirmButtonText: "Help",
                        focusConfirm: false,
                    }).then(result => {
                        if (result.value) {
                            this.showClaimInfoModal(true);
                        }
                    });
                    helpers.toggleSpinner(`action-${invoiceId}`);
                });
        },

        setSuccess(message = null) {
            this.success = message;
        },

        viewInvoice(invoice) {
            this.fetchInvoice(invoice.id);
            this.$refs.invoiceModal.visible = true;
        },

        async editInvoice(invoice, newContract = false) {
            if (newContract) {
                this.$refs.invoiceMiscCharge.newContract = newContract;
            }
            await this.fetchClaim(invoice.id);
            helpers.toggleSpinner(`action-${invoice.id}`);
            axios
                .get(
                    `/api/organizations/${this.organization.id}/invoices/${invoice.id}/charges/${
                        invoice.charges[0].id
                    }`
                )
                .then(response => {
                    const charge = response.data.data;
                    this.$refs.invoiceMiscCharge.parseEditPayload(invoice, charge);
                    helpers.toggleSpinner(`action-${invoice.id}`);
                    this.$refs.invoiceMiscCharge.visible = true;
                });
        },

        deleteInvoice(invoice) {
            axios
                .delete(`/api/organizations/${this.organization.id}/invoices/${invoice.id}`)
                .then(response => {
                    this.$emit("fetch-page");
                    this.resetDelete();
                })
                .catch(error => {
                    this.resetDelete();
                    this.setErrors({ Error: [error] });
                    console.error(error);
                });
        },

        async viewClaim(invoice) {
            await this.fetchClaim(invoice.id);
            this.$refs.claimModal.invoice = invoice;
            this.$refs.claimModal.visible = true;
        },

        fetchInvoice(id) {
            axios.get(`/api/organizations/${this.organization.id}/invoices/${id}`).then(r => {
                this.invoice = r.data.data;
            });
        },

        async fetchInvoiceAsync(id) {
            const response = await axios.get(
                `/api/organizations/${this.organization.id}/invoices/${id}`
            );
            return _.get(response, "data.data", null);
        },

        async updateInvoice(event) {
            await this.fetchClaim(event);
            this.$emit("fetch-page");
        },

        async fetchClaim(id) {
            const response = await axios.get(
                `/api/organizations/${this.organization.id}/invoices/${id}/claims`
            );
            this.claims = response.data.data;
        },

        exportInvoice() {
            this.loadingDownloadFunding = true;
            this.exportError = null;
            let url = `organizations/${this.organization.id}/invoices/export-invoices`;
            if (this.sourceType === "paid") {
                url += "?is_archived=1";
            } else {
                url += `?source_type=${this.sourceType}`;
            }
            axios({
                url: url,
                method: "GET",
                responseType: "blob",
            })
                .then(response => {
                    this.loadingDownloadFunding = false;
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", `${this.sourceType} Export.xlsx`); //or any other extension
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(e => {
                    console.error(e);
                    this.exportError = "No data to download";
                    this.loadingDownloadFunding = false;
                });
        },

        showClaimInfoModal(state) {
            this.isClaimInfoModalVisible = state;
        },

        badInvoiceData(invoice, title) {
            if (title !== "Submit claim") return false;
            if (!this.title === "Insurance Providers") return false;
            let message = "";
            let diagnoses = [];

            if (Array.isArray(invoice.charges)) {
                invoice.charges.forEach(charge => {
                    if (Array.isArray(charge.diagnoses)) {
                        charge.diagnoses.forEach(diagnosis => {
                            diagnoses.push(diagnosis);
                        });
                    }
                });
            }

            if (!diagnoses.length) {
                message += "The claim can not be submitted because there are no diagnosis codes. ";
            }

            if (
                !invoice.serviced_at ||
                this.moment(invoice.serviced_at).isSameOrAfter(
                    this.moment()
                        .hour(23)
                        .minute(59)
                        .second(59)
                )
            ) {
                message +=
                    "The claim can not be submitted because the service date is not set or is in the future.";
            }
            return message;
        },

        isTherapistArchived(therapistId) {
            console.log(therapistId);
            return !this.availableTherapistIds.includes(therapistId);
        },
    },
};
</script>

<style scoped lang="scss">
.table-responsive {
    overflow-y: visible !important;
}
.list-item {
    border: solid 1px #bfbfbf;
}
.list-item:not(:first-child) {
    border-top: none;
}

.pending,
.hold {
    background: blue;
    color: white;
}

.rejected {
    background: red;
    color: white;
}

.partial_rejection,
.approved {
    background: orange;
    color: black;
    white-space: normal;
    max-width: 60px;
}

.paid {
    background: darkgreen;
    color: white;
}

.canceled {
    background: grey;
    color: black;
}

.acknowledged {
    background: darkorchid;
    color: white;
}
</style>
